import axios, {AxiosResponse} from 'axios'
import {ID, Response} from '../../../../_metronic/helpers'
import {User, UsersQueryResponse} from './users-list/core/_models'
import {Directory} from './directory-list/core/_models'
import {Event} from './events-list/core/_models'
import {Skill, SkillsQueryResponse} from './skills-list/core/_models'
import {Interest, InterestsQueryResponse} from './interests-list/core/_models'
import {Job, Applicant} from './job-list/core/_models'
import {Role, RolesQueryResponse} from './roles/core/_models'
import {BlockUser} from './block-unblock-user/core/_models'
import {Reel, ReelsQueryResponse} from './reels-list/core/_models'
import {Game} from './game-list/core/_models'
import type {ActivityLogsParams} from './activity/core/_models'

const API_URL = process.env.REACT_APP_API_URL

// User List API
const getUsers = (query?: string): Promise<UsersQueryResponse> => {
  const mappedQuery = query ? query.replace(/items_per_page=/g, 'limit=') : ''
  const url = `${API_URL}/auth/users${mappedQuery ? `?${mappedQuery}` : ''}`

  return axios.get(url).then((response: AxiosResponse<any>) => {
    const resp = response.data

    const users = resp?.responseObject?.users || []
    const pagination = resp?.responseObject?.pagination || {
      page: 1,
      limit: 15,
      total: users.length,
      total_pages: 1,
    }

    // Build links array compatible with the frontend pagination component
    const totalPages = pagination.total_pages || 1
    const currentPage = pagination.page || 1

    const links: Array<{label: string; active: boolean; url: string | null; page: number | null}> =
      []

    // Previous link
    links.push({
      label: '&laquo; Previous',
      active: false,
      url: null,
      page: currentPage > 1 ? currentPage - 1 : null,
    })

    // Page number links
    for (let i = 1; i <= totalPages; i++) {
      links.push({label: String(i), active: i === currentPage, url: null, page: i})
    }

    // Next link
    links.push({
      label: 'Next &raquo;',
      active: false,
      url: null,
      page: currentPage < totalPages ? currentPage + 1 : null,
    })

    return {
      data: users,
      payload: {
        pagination: {
          page: currentPage,
          items_per_page: pagination.limit,
          total: pagination.total,
          total_pages: totalPages,
          links,
        },
      },
    } as UsersQueryResponse
  })
}

// Get user list for create/update event
const getUsersListForEvent = (): Promise<any> => {
  return axios
    .get(`${API_URL}/auth/users?page=1&limit=1000000`)
    .then((response: AxiosResponse<any>) => {
      const resp = response.data
      return resp?.responseObject ?? resp
    })
}
const getUsersListForJob = (): Promise<any> => {
  return axios
    .get(`${API_URL}/auth/users?page=1&limit=1000000&user_type=service_provider`)
    .then((response: AxiosResponse<any>) => {
      const resp = response.data
      return resp?.responseObject ?? resp
    })
}

const getUsersByUserType = (userType: string): Promise<any[]> => {
  const fetchAllUsers = async () => {
    const firstResponse = await axios.get(
      `${API_URL}/auth/users?page=1&limit=100&user_type=${userType}`
    )
    const firstResp = firstResponse.data
    const firstPageUsers = firstResp?.responseObject?.users || []
    const pagination = firstResp?.responseObject?.pagination || {}
    const totalPages = pagination.total_pages || 1

    if (totalPages <= 1) {
      return firstPageUsers
    }

    const pageRequests: Promise<AxiosResponse<any>>[] = []
    for (let page = 2; page <= totalPages; page++) {
      pageRequests.push(
        axios.get(`${API_URL}/auth/users?page=${page}&limit=100&user_type=${userType}`)
      )
    }

    const remainingResponses = await Promise.all(pageRequests)
    const remainingUsers = remainingResponses.flatMap((response) => {
      const resp = response.data
      return resp?.responseObject?.users || []
    })

    return [...firstPageUsers, ...remainingUsers]
  }

  return fetchAllUsers().catch((error) => {
    console.error(`Failed to fetch users for user type: ${userType}`, error)
    throw error
  })
}
// Get role list for users
const getRoleListForUsers = (): Promise<any> => {
  return axios.get(`${API_URL}/masters/role-modules`).then((response: AxiosResponse<any>) => {
    const resp = response.data
    return resp?.responseObject ?? resp
  })
}

// User Details API
const getUserById = (id: ID): Promise<User | undefined> => {
  return axios.get(`${API_URL}/auth/users/${id}`).then((response: AxiosResponse<any>) => {
    const resp = response.data
    return resp?.responseObject ?? resp
  })
}

// Assign Role & Approve User API
const assignRole = (data: {ndis_number; role}): Promise<User | undefined> => {
  return axios
    .post(`${API_URL}/auth/admin/approve-user`, data)
    .then((response: AxiosResponse<Response<User>>) => response.data)
    .then((response: Response<User>) => response.data)
}

// Approve / Reject User API
const approveUser = (data: {user_id; action}): Promise<User | undefined> => {
  return axios
    .post(`${API_URL}/auth/admin/approve-user`, data)
    .then((response: AxiosResponse<Response<User>>) => response.data)
    .then((response: Response<User>) => response.data)
}

const createUser = (data: any): Promise<User | undefined> => {
  return (
    axios
      // .post(`${API_URL}/profile/admin/create-user`, data)
      .post(`${API_URL}/profile/create-profile-v2`, data)
      .then((response: AxiosResponse<any>) => {
        const resp = response.data
        return resp
      })
  )
}

const updateUser = (data: any, id: any): Promise<any> => {
  return axios
    .put(`${API_URL}/profile/admin/edit-profile/${id}`, data)
    .then((response: AxiosResponse<any>) => {
      const resp = response.data
      return resp
    })
}

const updateStatus = (userId: ID, data: any): Promise<any> => {
  console.log(userId, data)
  return axios
    .patch(`${API_URL}/auth/admin/update-user-status/${userId}`, data)
    .then((response: AxiosResponse<any>) => {
      const resp = response.data
      return resp
    })
}

// Business sector get API
const getBusinessSector = (): Promise<any> => {
  return axios.get(`${API_URL}/other/business-sectors`).then((response: AxiosResponse<any>) => {
    const resp = response.data
    // assume API returns object under responseObject or directly
    return resp?.responseObject || resp
  })
}

// Business type get API
const getBusinessType = (): Promise<any> => {
  return axios.get(`${API_URL}/other/business-types`).then((response: AxiosResponse<any>) => {
    const resp = response.data
    // assume API returns object under responseObject or directly
    return resp?.responseObject || resp
  })
}

const deleteUser = (userId: ID): Promise<void> => {
  return axios.delete(`${API_URL}/profile/admin/delete-user/${userId}`).then(() => {})
}

const deleteSelectedUsers = (userIds: Array<ID>): Promise<void> => {
  const requests = userIds.map((id) => axios.delete(`${API_URL}/${id}`))
  return axios.all(requests).then(() => {})
}

// NDIS Verification API
const isNDISVerification = (data: {ndis_number: string; is_ndis_verified: boolean}) => {
  return axios.patch(`${API_URL}/auth/admin/edit-ndis`, data).then((res) => res.data)
}

// Roles API
const getRoles = (query?: string): Promise<RolesQueryResponse> => {
  const mappedQuery = query ? query.replace(/items_per_page=/g, 'limit=') : ''
  const url = `${API_URL}/auth/roles${mappedQuery ? `?${mappedQuery}` : ''}`

  return axios.get(url).then((response: AxiosResponse<any>) => {
    const resp = response.data

    const roles = resp?.responseObject?.roles || []
    const pagination = resp?.responseObject?.pagination || {
      page: 1,
      limit: 15,
      total: roles.length,
      total_pages: 1,
    }

    // Build links array compatible with the frontend pagination component
    const totalPages = pagination.total_pages || 1
    const currentPage = pagination.page || 1

    const links: Array<{label: string; active: boolean; url: string | null; page: number | null}> =
      []

    // Previous link
    links.push({
      label: '&laquo; Previous',
      active: false,
      url: null,
      page: currentPage > 1 ? currentPage - 1 : null,
    })

    // Page number links
    for (let i = 1; i <= totalPages; i++) {
      links.push({label: String(i), active: i === currentPage, url: null, page: i})
    }

    // Next link
    links.push({
      label: 'Next &raquo;',
      active: false,
      url: null,
      page: currentPage < totalPages ? currentPage + 1 : null,
    })

    return {
      data: roles,
      payload: {
        pagination: {
          page: currentPage,
          items_per_page: pagination.limit,
          total: pagination.total,
          total_pages: totalPages,
          links,
        },
      },
    } as RolesQueryResponse
  })
}

const getRoleById = (id: ID): Promise<Role | undefined> => {
  return axios.get(`${API_URL}/auth/roles/${id}`).then((response: AxiosResponse<any>) => {
    const resp = response.data
    return resp?.responseObject ?? resp
  })
}

const createRole = (role: Role): Promise<Role | undefined> => {
  return axios
    .put(`${API_URL}/auth/roles`, role)
    .then((response: AxiosResponse<Response<Role>>) => response.data)
    .then((response: Response<Role>) => response.data)
}

const updateRole = (role: Role): Promise<Role | undefined> => {
  return axios
    .post(`${API_URL}/auth/roles/${role.id}`, role)
    .then((response: AxiosResponse<Response<Role>>) => response.data)
    .then((response: Response<Role>) => response.data)
}

const deleteRole = (roleId: ID): Promise<void> => {
  return axios.delete(`${API_URL}/auth/roles/${roleId}`).then(() => {})
}

const deleteSelectedRoles = (roleIds: Array<ID>): Promise<void> => {
  const requests = roleIds.map((id) => axios.delete(`${API_URL}/auth/roles/${id}`))
  return axios.all(requests).then(() => {})
}

// Service Providers List API
const getServiceProviders = (query?: string): Promise<Response<Array<Directory>>> => {
  // Backend expects limit & offset or limit & page; map items_per_page to limit
  const mappedQuery = query ? query.replace(/items_per_page=/g, 'limit=') : ''
  const url = `${API_URL}/service-providers${mappedQuery ? `?${mappedQuery}` : ''}`

  return axios.get(url).then((response: AxiosResponse<any>) => {
    const resp = response.data

    const items = resp?.responseObject?.data || resp?.responseObject || []
    const pagination = resp?.responseObject?.pagination || {
      page: 1,
      limit: 15,
      total: items.length,
      total_pages: 1,
    }

    const totalPages = pagination.total_pages || pagination.totalPages || 1
    const currentPage = pagination.page || pagination.currentPage || 1

    const links: Array<{label: string; active: boolean; url: string | null; page: number | null}> =
      []

    links.push({
      label: '&laquo; Previous',
      active: false,
      url: null,
      page: currentPage > 1 ? currentPage - 1 : null,
    })

    for (let i = 1; i <= totalPages; i++) {
      links.push({label: String(i), active: i === currentPage, url: null, page: i})
    }

    links.push({
      label: 'Next &raquo;',
      active: false,
      url: null,
      page: currentPage < totalPages ? currentPage + 1 : null,
    })

    const mapped: Array<Directory> = (items || []).map((it: any) => ({
      id: Number(it.id ?? it.provider_id) || 0,
      name: it.name || it.first_name || '',
      business_name: it.business_name || it.company_name || '',
      phone: it.phone || it.mobile || '',
      email: it.email || it.contact_email || '',
      location: it.location || it.address || '',
      created_at: it.created_at || it.createdAt || '',
    }))

    return {
      data: mapped,
      payload: {
        pagination: {
          page: currentPage,
          items_per_page: pagination.limit || pagination.items_per_page || pagination.limit,
          total: pagination.total || 0,
          total_pages: totalPages,
          links,
        },
      },
    } as Response<Array<Directory>>
  })
}

// Service Provider details API
const getServiceProviderById = (id: ID): Promise<any> => {
  return axios.get(`${API_URL}/service-providers/${id}`).then((response: AxiosResponse<any>) => {
    const resp = response.data
    // assume API returns object under responseObject or directly
    return resp?.responseObject || resp
  })
}

// Events List API
const getEvents = (query?: string): Promise<Response<Array<Event>>> => {
  const mappedQuery = query ? query.replace(/items_per_page=/g, 'limit=') : ''
  const url = `${API_URL}/events${mappedQuery ? `?${mappedQuery}` : ''}`

  return axios.get(url).then((response: AxiosResponse<any>) => {
    const resp = response.data

    const events = resp?.responseObject?.data || []
    const pagination = resp?.responseObject?.pagination || {
      currentPage: 1,
      limit: 15,
      total: events.length,
      totalPages: 1,
    }

    // Build links array compatible with the frontend pagination component
    const totalPages = pagination.totalPages || 1
    const currentPage = pagination.currentPage || 1

    const links: Array<{label: string; active: boolean; url: string | null; page: number | null}> =
      []

    // Previous link
    links.push({
      label: '&laquo; Previous',
      active: false,
      url: null,
      page: currentPage > 1 ? currentPage - 1 : null,
    })

    // Page number links
    for (let i = 1; i <= totalPages; i++) {
      links.push({label: String(i), active: i === currentPage, url: null, page: i})
    }

    // Next link
    links.push({
      label: 'Next &raquo;',
      active: false,
      url: null,
      page: currentPage < totalPages ? currentPage + 1 : null,
    })

    const eventsMapped: Array<Event> = events.map((event: any) => ({
      id: event.id?.toString() || '',
      name: event.title || '',
      date: event.event_start_datetime || '',
      location: event.location_name || (event.is_online ? 'Online' : 'Offline'),
      status: event.status || '',
      created_at: event.created_at || '',
      description: event.description || '',
      fee: event.fee,
    }))

    return {
      data: eventsMapped,
      payload: {
        pagination: {
          page: currentPage,
          items_per_page: pagination.limit,
          total: pagination.total,
          total_pages: totalPages,
          links,
        },
      },
    } as Response<Array<Event>>
  })
}
const getJobs = (query?: string): Promise<Response<Array<Job>>> => {
  const mappedQuery = query ? query.replace(/items_per_page=/g, 'limit=') : ''
  const url = `${API_URL}/jobs${mappedQuery ? `?${mappedQuery}` : ''}`

  return axios.get(url).then((response: AxiosResponse<any>) => {
    const resp = response.data

    const jobs = resp?.responseObject?.data || []
    const pagination = resp?.responseObject?.pagination || {
      page: 1,
      limit: 15,
      total: jobs.length,
      total_pages: 1,
    }

    // Build links array compatible with the frontend pagination component
    const totalPages = pagination.total_pages || 1
    const currentPage = pagination.page || 1

    const links: Array<{label: string; active: boolean; url: string | null; page: number | null}> =
      []

    // Previous link
    links.push({
      label: '&laquo; Previous',
      active: false,
      url: null,
      page: currentPage > 1 ? currentPage - 1 : null,
    })

    // Page number links
    for (let i = 1; i <= totalPages; i++) {
      links.push({label: String(i), active: i === currentPage, url: null, page: i})
    }

    // Next link
    links.push({
      label: 'Next &raquo;',
      active: false,
      url: null,
      page: currentPage < totalPages ? currentPage + 1 : null,
    })

    const jobsMapped: Array<Job> = jobs.map((job: any) => ({
      id: job.id?.toString() || '',
      title: job.title || job.job_title || '',
      companyName: job.company_name || job.companyName || '',
      jobType: job.job_type || job.jobType || '',
      location: job.location || '',
      locations: Array.isArray(job.locations)
        ? job.locations.map((loc: any) => loc.location_name || '').filter((name: string) => name)
        : [],
      salary: job.salary || '',
      compensation_min: job.compensation_min || job.min_salary || null,
      compensation_max: job.compensation_max || job.max_salary || null,
      status: job.status || '',
      createdDate: job.created_at || job.createdDate || '',
      description: job.description || '',
      approval_status: job.approval_status || job.approvalStatus || '',
      is_active:
        job.is_active !== undefined
          ? job.is_active
          : job.isActive !== undefined
            ? job.isActive
            : true,
      applicants_count: job.applicants_count || 0,
    }))

    return {
      data: jobsMapped,
      payload: {
        pagination: {
          page: currentPage,
          items_per_page: pagination.limit,
          total: pagination.total,
          total_pages: totalPages,
          links,
        },
      },
    } as Response<Array<any>>
  })
}

// Get job details by id
const getJobById = (id: ID): Promise<any> => {
  return axios.get(`${API_URL}/jobs/${id}`).then((response: AxiosResponse<any>) => {
    const resp = response.data
    return resp?.responseObject ?? resp
  })
}

const createJob = (job: any): Promise<any> => {
  return axios.post(`${API_URL}/jobs`, job).then((response: AxiosResponse<any>) => {
    const resp = response.data
    return resp
  })
}

const updateJob = (id: ID, job: any): Promise<any> => {
  return axios.put(`${API_URL}/jobs/${id}`, job).then((response: AxiosResponse<any>) => {
    const resp = response.data
    return resp
  })
}

// Approve / Reject Job API
const approveJob = (data: {job_id; action}): Promise<Job | undefined> => {
  return axios
    .put(`${API_URL}/jobs/${data.job_id}/status`, {status: data.action})
    .then((response: AxiosResponse<any>) => {
      const resp = response.data
      return resp
    })
}

// Update Job Status API
const updateJobStatus = (jobId: ID, data: any): Promise<any> => {
  return axios
    .patch(`${API_URL}/jobs/${jobId}/active-status`, data)
    .then((response: AxiosResponse<any>) => {
      const resp = response.data
      return resp
    })
}

// Event Categories API
const getEventCategories = (): Promise<any> => {
  return axios.get(`${API_URL}/event-categories`).then((response: AxiosResponse<any>) => {
    const resp = response.data
    return resp?.responseObject?.data ?? resp
  })
}

// Event Details API
const getEventById = (id: ID): Promise<any> => {
  return axios.get(`${API_URL}/events/${id}`).then((response: AxiosResponse<any>) => {
    const resp = response.data
    return resp?.responseObject ?? resp
  })
}

const createEvent = (event: Event): Promise<any> => {
  return axios.post(`${API_URL}/events`, event).then((response: AxiosResponse<any>) => {
    const resp = response.data
    return resp
  })
}
const createGame = (data: any): Promise<any> => {
  return axios.post(`${API_URL}/games`, data).then((response: AxiosResponse<any>) => {
    const resp = response.data
    return resp
  })
}
const getGameById = (id: ID): Promise<any> => {
  return axios.get(`${API_URL}/games/${id}`).then((response: AxiosResponse<any>) => {
    const resp = response.data
    return resp?.responseObject || resp
  })
}

const updateGame = (game: any, id: any): Promise<any> => {
  return axios.put(`${API_URL}/games/${id}`, game).then((response: AxiosResponse<any>) => {
    const resp = response.data
    return resp
  })
}

const toggleGameInterest = (gameId: ID): Promise<any> => {
  return axios.post(`${API_URL}/games/${gameId}/interest`).then((response: AxiosResponse<any>) => {
    const resp = response.data
    return resp
  })
}

const updateGameStatus = (id: any, data: {is_active: number}): Promise<any> => {
  return axios.put(`${API_URL}/games/${id}/status`, data).then((response: AxiosResponse<any>) => {
    const resp = response.data
    return resp
  })
}

const updateEvent = (event: Event, id: any): Promise<any> => {
  return axios.put(`${API_URL}/events/${id}`, event).then((response: AxiosResponse<any>) => {
    const resp = response.data
    return resp
  })
}

const deleteEvent = (eventId: ID): Promise<void> => {
  return axios.delete(`${API_URL}/events/${eventId}`).then(() => {})
}

const deleteSelectedEvents = (eventIds: Array<ID>): Promise<void> => {
  const requests = eventIds.map((id) => axios.delete(`${API_URL}/events/${id}`))
  return axios.all(requests).then(() => {})
}

const eventApproval = (eventId: ID, status: 'A' | 'R', remarks: string): Promise<any> => {
  return axios.patch(`${API_URL}/events/${eventId}/approval`, {
    status,
    remarks,
  })
}

const rejectEvent = (eventId: ID, reason: string): Promise<any> => {
  return axios.patch(`${API_URL}/events/${eventId}/approval`, {
    status: 'R',
    remarks: reason,
  })
}

// role list api
const getRoleList = (): Promise<any> => {
  return axios.get(`${API_URL}/masters/role-modules`).then((response: AxiosResponse<any>) => {
    const resp = response.data
    return resp?.responseObject ?? resp
  })
}

const getPermissionList = (): Promise<any> => {
  // return axios
  //   .get(`${API_URL}/masters/permissions`)
  //   .then((response: AxiosResponse<any>) => {
  //     const resp = response.data
  //     return resp?.responseObject ?? resp
  //   })
  return getRoleList()
}

const getRoleView = (id: string): Promise<any> => {
  return axios.get(`${API_URL}/masters/roles/${id}`).then((response: AxiosResponse<any>) => {
    const resp = response.data
    return resp?.responseObject ?? resp
  })
}

const getPermissionView = (id: string): Promise<any> => {
  // return axios
  //   .get(`${API_URL}/masters/permissions/${id}`)
  //   .then((response: AxiosResponse<any>) => {
  //     const resp = response.data
  //     return resp?.responseObject ?? resp
  //   })
  return getRoleView(id)
}

// modules list api
const getModules = (): Promise<any> => {
  return axios.get(`${API_URL}/masters/modules`).then((response: AxiosResponse<any>) => {
    const resp = response.data
    return resp?.responseObject ?? resp
  })
}

// create master role api
const createMasterRole = (data: {name: string; slug: string; modules: number[]}): Promise<any> => {
  return axios.post(`${API_URL}/masters/roles`, data).then((response: AxiosResponse<any>) => {
    const resp = response.data
    return resp?.responseObject ?? resp
  })
}

const createMasterPermission = (data: {
  name: string
  slug: string
  modules: number[]
}): Promise<any> => {
  // return axios
  //   .post(`${API_URL}/masters/permissions`, data)
  //   .then((response: AxiosResponse<any>) => {
  //     const resp = response.data
  //     return resp?.responseObject ?? resp
  //   })
  return createMasterRole(data)
}

// update master role api
const updateMasterRole = (
  id: number | string,
  data: {name: string; slug: string; modules: number[]}
): Promise<any> => {
  return axios.put(`${API_URL}/masters/roles/${id}`, data).then((response: AxiosResponse<any>) => {
    const resp = response.data
    return resp?.responseObject ?? resp
  })
}

const updateMasterPermission = (
  id: number | string,
  data: {name: string; slug: string; modules: number[]}
): Promise<any> => {
  // return axios
  //   .put(`${API_URL}/masters/permissions/${id}`, data)
  //   .then((response: AxiosResponse<any>) => {
  //     const resp = response.data
  //     return resp?.responseObject ?? resp
  //   })
  return updateMasterRole(id, data)
}

// update master role status api
const updateMasterRoleStatus = (id: number | string, isActive: boolean): Promise<any> => {
  return axios
    .patch(`${API_URL}/masters/roles/${id}/status`, {is_active: isActive})
    .then((response: AxiosResponse<any>) => {
      const resp = response.data
      return resp?.responseObject ?? resp
    })
}

const updateMasterPermissionStatus = (id: number | string, isActive: boolean): Promise<any> => {
  // return axios
  //   .patch(`${API_URL}/masters/permissions/${id}/status`, { is_active: isActive })
  //   .then((response: AxiosResponse<any>) => {
  //     const resp = response.data
  //     return resp?.responseObject ?? resp
  //   })
  return updateMasterRoleStatus(id, isActive)
}

// update role status api (admin)
const updateRoleStatus = (roleId: ID, data: any): Promise<any> => {
  console.log(roleId, data)
  return axios
    .patch(`${API_URL}/masters/roles/${roleId}`, data)
    .then((response: AxiosResponse<any>) => {
      const resp = response.data
      return resp
    })
}

// Forcefully logout API
const isForcefullyLogout = (data: any) => {
  return axios.patch(`${API_URL}/profile/admin/force-logout-by-type`, data).then((res) => res.data)
}

// User Types API
// const getUserTypes = (): Promise<any> => {
//   return axios
//     .get(`${API_URL}/masters/user-types`)
//     .then((response: AxiosResponse<any>) => {
//       const resp = response.data
//       return resp?.responseObject ?? resp
//     })
// }

// Event Categories List API
const getEventCategoriess = (query?: string): Promise<any> => {
  const mappedQuery = query ? query.replace(/items_per_page=/g, 'limit=') : ''
  const url = `${API_URL}/event-categories${mappedQuery ? `?${mappedQuery}` : ''}`

  return axios.get(url).then((response: AxiosResponse<any>) => {
    const resp = response.data

    const categoriesRaw = resp?.responseObject?.data || resp?.responseObject || []
    const categories = categoriesRaw.map((cat: any) => ({
      id: cat?.id,
      name: cat?.name || '',
      description: cat?.description || '',
      is_active: cat?.is_active,
      status: cat?.status || 'active',
      created_at: cat?.created_at || '',
    }))

    const apiPagination = resp?.responseObject?.pagination || {}
    const totalPages = apiPagination.totalPages || 1
    const currentPage = apiPagination.currentPage || 1

    const links: Array<{
      label: string
      active: boolean
      url: string | null
      page: number | null
    }> = []

    // Previous link
    links.push({
      label: '&laquo; Previous',
      active: false,
      url: null,
      page: currentPage > 1 ? currentPage - 1 : null,
    })

    // Page number links
    for (let i = 1; i <= totalPages; i++) {
      links.push({label: String(i), active: i === currentPage, url: null, page: i})
    }

    // Next link
    links.push({
      label: 'Next &raquo;',
      active: false,
      url: null,
      page: currentPage < totalPages ? currentPage + 1 : null,
    })

    return {
      data: categories,
      payload: {
        pagination: {
          page: currentPage,
          items_per_page: apiPagination.limit || 15,
          total: apiPagination.total || categories.length,
          total_pages: totalPages,
          links,
        },
      },
    }
  })
}
// Event Category by ID (for edit)
const getEventCategoryById = (id: ID): Promise<any> => {
  return axios.get(`${API_URL}/event-categories/${id}`).then((response: AxiosResponse<any>) => {
    const resp = response.data
    return resp?.responseObject ?? resp
  })
}

// Create Event Category
const createEventCategory = (data: {
  name: string
  description: string
  is_active: boolean
}): Promise<any> => {
  return axios.post(`${API_URL}/event-categories`, data).then((response: AxiosResponse<any>) => {
    const resp = response.data
    return resp?.responseObject ?? resp
  })
}
// Event Category Status (Admin Approve) - PATCH /event-categories/:id/status
const updateEventCategoryStatus = (id: string | number, isApproved: boolean): Promise<any> => {
  const status = isApproved ? 'approved' : 'rejected'
  return axios
    .patch(`${API_URL}/event-categories/${id}/status`, {status})
    .then((response: AxiosResponse<any>) => {
      const resp = response.data
      return resp?.responseObject ?? resp
    })
}

const updateEventCategory = (
  id: string | number,
  data: {
    name: string
    description: string
    is_active: boolean
  }
): Promise<any> => {
  const payload = {
    ...data,
    is_active: Boolean(data.is_active),
  }

  return axios
    .put(`${API_URL}/event-categories/${id}`, payload)
    .then((response: AxiosResponse<any>) => {
      const resp = response.data
      console.log('resp', resp)
      return resp?.responseObject ?? resp
    })
}

// Interest Suggestions API
const getInterestSuggestions = (keyword: string): Promise<any> => {
  return axios
    .get(`${API_URL}/profile/interests/suggestions?keyword=${keyword}`)
    .then((response: AxiosResponse<any>) => {
      const resp = response.data
      return resp
    })
}

// Interest Service Suggestions API
const getServiceSuggestions = (keyword: string): Promise<any> => {
  return axios
    .get(`${API_URL}/profile/services/suggestions?keyword=${keyword}`)
    .then((response: AxiosResponse<any>) => {
      const resp = response.data
      return resp
    })
}

// Skill Suggestions API
const getSkillSuggestions = (keyword: string): Promise<any> => {
  return axios
    .get(`${API_URL}/admin/skills/suggestions?name=${keyword}`)
    .then((response: AxiosResponse<any>) => {
      const resp = response.data
      return resp
    })
}

// Block Users List API
const getBlockUsers = (query?: string): Promise<Response<Array<BlockUser>>> => {
  const mappedQuery = query ? query.replace(/items_per_page=/g, 'limit=') : ''
  const url = `${API_URL}/block/admin/blocked-users${mappedQuery ? `?${mappedQuery}` : ''}`

  return axios.get(url).then((response: AxiosResponse<any>) => {
    const resp = response.data

    const blockUsers = resp?.responseObject?.data || []
    const pagination = resp?.responseObject?.pagination || {
      currentPage: 1,
      limit: 15,
      total: blockUsers.length,
      totalPages: 1,
    }

    // Build links array compatible with the frontend pagination component
    const totalPages = pagination.totalPages || 1
    const currentPage = pagination.currentPage || 1

    const links: Array<{label: string; active: boolean; url: string | null; page: number | null}> =
      []

    // Previous link
    links.push({
      label: '&laquo; Previous',
      active: false,
      url: null,
      page: currentPage > 1 ? currentPage - 1 : null,
    })

    // Page number links
    for (let i = 1; i <= totalPages; i++) {
      links.push({label: String(i), active: i === currentPage, url: null, page: i})
    }

    // Next link
    links.push({
      label: 'Next &raquo;',
      active: false,
      url: null,
      page: currentPage < totalPages ? currentPage + 1 : null,
    })

    const blockUsersMapped: Array<BlockUser> = blockUsers.map((user: any) => ({
      id: user.id?.toString() || '',
      blocker_name: user.blocker_name || '',
      blocked_name: user.blocked_name || '',
      reason: user.reason || user.block_reason || '',
      blocked_at: user.blocked_at || user.blocked_date || user.blockedAt || '',
    }))

    return {
      data: blockUsersMapped,
      payload: {
        pagination: {
          page: currentPage,
          items_per_page: pagination.limit,
          total: pagination.total,
          total_pages: totalPages,
          links,
        },
      },
    } as Response<Array<BlockUser>>
  })
}

// Block User API
const blockUser = (payload: {
  blocker_id: number
  blocked_id: number
  reason: string
  description: ''
}): Promise<any> => {
  return axios
    .post(`${API_URL}/block/admin/block-user`, payload)
    .then((response: AxiosResponse<any>) => response.data)
}

// Get user list for block user
const getBlockerUsers = (): Promise<any> => {
  return axios.get(`${API_URL}/block/admin/users-list`).then((response: AxiosResponse<any>) => {
    const resp = response.data
    return resp?.responseObject ?? resp
  })
}

// Get user list for blocker friends user
const getBlockedUsers = (id: ID): Promise<any> => {
  return axios
    .get(`${API_URL}/block/admin/user-friends/${id}`)
    .then((response: AxiosResponse<any>) => {
      const resp = response.data
      return resp?.responseObject ?? resp
    })
}

// Unblock User API
const unblockUser = (id: ID): Promise<any> => {
  return axios
    .delete(`${API_URL}/block/admin/unblock-user/${id}`)
    .then((response: AxiosResponse<any>) => response.data)
}

// Skills List API
const getSkills = (query?: string): Promise<SkillsQueryResponse> => {
  const mappedQuery = query ? query.replace(/items_per_page=/g, 'limit=') : ''
  const params = new URLSearchParams(mappedQuery)
  if (params.has('search')) {
    params.set('name', params.get('search') || '')
    params.delete('search')
  }
  const finalQuery = params.toString()
  const url = `${API_URL}/admin/skills/suggestions${finalQuery ? `?${finalQuery}` : ''}`
  // const searchTerm = new URLSearchParams(finalQuery).get('name')?.trim().toLowerCase() || ''

  return axios.get(url).then((response: AxiosResponse<any>) => {
    const resp = response.data

    const skillsRaw = Array.isArray(resp?.responseObject?.data) ? resp.responseObject.data : []
    const normalizedSkills: Array<Skill> = skillsRaw.map((skill: any) => {
      const normalizedStatus = String(skill?.status || '').toLowerCase()

      return {
        id: skill?.id,
        name: skill?.name || '',
        category: skill?.category ?? null,
        category_name: skill?.category ?? '',
        status: normalizedStatus,
        approval_status: normalizedStatus,
        is_active: skill?.is_active,
        created_at: skill?.created_at || '',
        updated_at: skill?.updated_at || '',
      }
    })
    const skills = normalizedSkills

    const apiPagination = resp?.responseObject?.pagination || {}
    let pagination = {
      page: apiPagination?.page || 1,
      limit: apiPagination?.limit || skills.length || 15,
      total: apiPagination?.total || skills.length,
      total_pages: apiPagination?.totalPages || 1,
    }

    const totalPages = pagination.total_pages || 1
    const currentPage = pagination.page || 1

    const links: Array<{label: string; active: boolean; url: string | null; page: number | null}> =
      []

    links.push({
      label: '&laquo; Previous',
      active: false,
      url: null,
      page: currentPage > 1 ? currentPage - 1 : null,
    })

    for (let i = 1; i <= totalPages; i++) {
      links.push({label: String(i), active: i === currentPage, url: null, page: i})
    }

    links.push({
      label: 'Next &raquo;',
      active: false,
      url: null,
      page: currentPage < totalPages ? currentPage + 1 : null,
    })

    return {
      data: Array.isArray(skills) ? skills : [],
      payload: {
        pagination: {
          page: currentPage,
          items_per_page: pagination.limit,
          total: pagination.total,
          total_pages: totalPages,
          links,
        },
      },
    } as SkillsQueryResponse
  })
}

const getSkillById = (id: ID): Promise<any> => {
  return axios.post(`${API_URL}/admin/skills`, {id}).then((response: AxiosResponse<any>) => {
    const resp = response.data
    return resp?.responseObject?.data ?? resp?.responseObject ?? resp
  })
}

const createSkill = (skill: FormData | Skill): Promise<any> => {
  return axios.post(`${API_URL}/admin/skills`, skill).then((response: AxiosResponse<any>) => {
    const resp = response.data
    return resp
  })
}

const updateSkill = (skill: FormData | Skill, id: ID): Promise<any> => {
  return axios.put(`${API_URL}/admin/skills/${id}`, skill).then((response: AxiosResponse<any>) => {
    const resp = response.data
    return resp
  })
}

const updateSkillStatus = (skillId: ID, data: {is_active: boolean}): Promise<any> => {
  return axios
    .put(`${API_URL}/skills/${skillId}/active-status`, data)
    .then((response: AxiosResponse<any>) => {
      const resp = response.data
      return resp
    })
}

// const deleteSkill = (skillId: ID): Promise<void> => {
//   return axios.delete(`${API_URL}/skills/${skillId}`).then(() => {})
// }

// const deleteSelectedSkills = (skillIds: Array<ID>): Promise<void> => {
//   const requests = skillIds.map((id) => axios.delete(`${API_URL}/skills/${id}`))
//   return axios.all(requests).then(() => {})
// }

const skillApproval = (skillId: ID, status: string): Promise<any> => {
  return axios
    .put(`${API_URL}/skills/${skillId}/status`, {
      status,
    })
    .then((response: AxiosResponse<any>) => {
      const resp = response.data
      return resp
    })
}

const approveSkill = (skillId: ID): Promise<any> => {
  return skillApproval(skillId, 'Approved')
}

const rejectSkill = (skillId: ID): Promise<any> => {
  return skillApproval(skillId, 'Rejected')
}

// Interests List API
const getInterests = (query?: string): Promise<InterestsQueryResponse> => {
  const mappedQuery = query ? query.replace(/items_per_page=/g, 'limit=') : ''
  const params = new URLSearchParams(mappedQuery)
  const search = params.get('search')
  if (search !== null) {
    params.set('search', search.trim())
  }
  if (!params.has('sort')) params.set('sort', 'created_at')
  if (!params.has('order')) params.set('order', 'desc')

  const finalQuery = params.toString()
  const url = `${API_URL}/profile/admin/interests${finalQuery ? `?${finalQuery}` : ''}`

  return axios.get(url).then((response: AxiosResponse<any>) => {
    const resp = response.data
    const responseObject = resp?.responseObject || {}
    const rawInterests =
      responseObject?.data || responseObject?.interests || resp?.data || []
    const interests: Array<Interest> = Array.isArray(rawInterests)
      ? rawInterests.map((interest: any) => ({
          ...interest,
          id: interest?.id,
          name: interest?.name || '',
          approval_status: String(
            interest?.approval_status || interest?.status || 'pending'
          ).toLowerCase(),
          is_active: interest?.is_active,
          created_at: interest?.created_at || '',
          updated_at: interest?.updated_at || '',
        }))
      : []

    const apiPagination = responseObject?.pagination || resp?.pagination || {}
    const currentPage = Number(apiPagination?.page) || 1
    const limit = Number(apiPagination?.limit || apiPagination?.items_per_page) || 15
    const total = Number(apiPagination?.total) || interests.length
    const totalPages =
      Number(apiPagination?.total_pages || apiPagination?.totalPages) ||
      Math.max(1, Math.ceil(total / limit))
    const links: Array<{label: string; active: boolean; url: string | null; page: number | null}> = [
      {
        label: '&laquo; Previous',
        active: false,
        url: null,
        page: currentPage > 1 ? currentPage - 1 : null,
      },
    ]

    for (let page = 1; page <= totalPages; page++) {
      links.push({label: String(page), active: page === currentPage, url: null, page})
    }
    links.push({
      label: 'Next &raquo;',
      active: false,
      url: null,
      page: currentPage < totalPages ? currentPage + 1 : null,
    })

    return {
      data: interests,
      payload: {
        pagination: {
          page: currentPage,
          items_per_page: limit,
          total,
          total_pages: totalPages,
          links,
        },
      },
    } as InterestsQueryResponse
  })
}

const getInterestById = (id: ID): Promise<any> =>
  axios.get(`${API_URL}/profile/admin/interests/${id}`).then((response) => {
    const resp = response.data
    return resp?.responseObject?.data ?? resp?.responseObject ?? resp
  })

const createInterest = (interest: FormData | Interest): Promise<any> =>
  axios.post(`${API_URL}/profile/admin/interests`, interest).then((response) => response.data)

const updateInterest = (interest: FormData | Interest, id: ID): Promise<any> =>
  axios.put(`${API_URL}/profile/admin/interests/${id}`, interest).then((response) => response.data)

const updateInterestStatus = (interestId: ID, data: {is_active: boolean}): Promise<any> =>
  axios
    .put(`${API_URL}/profile/admin/interests/${interestId}/active-status`, data)
    .then((response) => response.data)

const interestApproval = (interestId: ID, status: string): Promise<any> =>
  axios
    .put(`${API_URL}/profile/admin/interests/${interestId}/status`, {status})
    .then((response) => response.data)

const approveInterest = (interestId: ID): Promise<any> =>
  interestApproval(interestId, 'Approved')

const rejectInterest = (interestId: ID): Promise<any> =>
  interestApproval(interestId, 'Rejected')

// Job Applicant List API
const getJobsApplicant = (jobId: ID, query?: string): Promise<Response<Array<Applicant>>> => {
  const mappedQuery = query ? query.replace(/items_per_page=/g, 'limit=') : ''
  const url = `${API_URL}/jobs/${jobId}/applicants${mappedQuery ? `?${mappedQuery}` : ''}`

  return axios.get(url).then((response: AxiosResponse<any>) => {
    const resp = response.data

    const applicants = resp?.responseObject?.data || []
    const pagination = resp?.responseObject?.pagination || {
      page: 1,
      limit: 15,
      total: applicants.length,
      total_pages: 1,
    }

    // Build links array compatible with the frontend pagination component
    const totalPages = pagination.total_pages || 1
    const currentPage = pagination.page || 1

    const links: Array<{label: string; active: boolean; url: string | null; page: number | null}> =
      []

    // Previous link
    links.push({
      label: '&laquo; Previous',
      active: false,
      url: null,
      page: currentPage > 1 ? currentPage - 1 : null,
    })

    // Page number links
    for (let i = 1; i <= totalPages; i++) {
      links.push({label: String(i), active: i === currentPage, url: null, page: i})
    }

    // Next link
    links.push({
      label: 'Next &raquo;',
      active: false,
      url: null,
      page: currentPage < totalPages ? currentPage + 1 : null,
    })

    const applicantsMapped: Array<Applicant> = applicants.map((applicant: any) => ({
      id: applicant.id,
      job_id: applicant.job_id,
      support_worker_id: applicant.support_worker_id,
      applied_at: applicant.applied_at,
      status: applicant.status,
      first_name: applicant.first_name,
      last_name: applicant.last_name,
      email: applicant.email,
      phone: applicant.phone,
      profile_picture_url: applicant.profile_picture_url,
      resume_file_url: applicant.resume_file_url,
      distance_km: applicant.distance_km,
    }))

    return {
      data: applicantsMapped,
      payload: {
        pagination: {
          page: currentPage,
          items_per_page: pagination.limit,
          total: pagination.total,
          total_pages: totalPages,
          links,
        },
      },
    } as Response<Array<any>>
  })
}
const getReels = (query?: string): Promise<ReelsQueryResponse> => {
  const mappedQuery = query ? query.replace(/items_per_page=/g, 'limit=') : ''
  const url = `${API_URL}/posts/feed/reels${mappedQuery ? `?${mappedQuery}` : ''}`

  return axios.get(url).then((response: AxiosResponse<any>) => {
    const resp = response.data
    const reelsRaw = resp?.responseObject?.data || []
    const reels: Reel[] = reelsRaw.map((reel: any) => ({
      ...reel,
      created_by: {
        id: reel.created_by?.id || 0,
        first_name: reel.created_by?.first_name || '',
        last_name: reel.created_by?.last_name || '',
        phone: reel.created_by?.phone || '',
        email: reel.created_by?.email || '',
      },
    }))
    const pagination = resp?.responseObject?.pagination || {
      page: 1,
      limit: 15,
      total: reels.length,
      totalPages: 1,
    }

    const totalPages = pagination.total_pages || pagination.totalPages || 1
    const currentPage = pagination.page || pagination.currentPage || 1

    const links: Array<{label: string; active: boolean; url: string | null; page: number | null}> =
      []

    // Previous link
    links.push({
      label: '&laquo; Previous',
      active: false,
      url: null,
      page: currentPage > 1 ? currentPage - 1 : null,
    })

    // Page number links (first 5 pages)
    const startPage = Math.max(1, currentPage - 2)
    const endPage = Math.min(totalPages, currentPage + 2)
    for (let i = startPage; i <= endPage; i++) {
      links.push({label: String(i), active: i === currentPage, url: null, page: i})
    }

    // Next link
    links.push({
      label: 'Next &raquo;',
      active: false,
      url: null,
      page: currentPage < totalPages ? currentPage + 1 : null,
    })

    return {
      data: reels,
      payload: {
        pagination: {
          page: currentPage,
          items_per_page: pagination.limit || pagination.items_per_page || 15,
          total: pagination.total || reels.length,
          total_pages: totalPages,
          links,
        },
      },
    } as ReelsQueryResponse
  })
}

const getReelById = (id: ID): Promise<Reel | undefined> => {
  return axios.get(`${API_URL}/reels/${id}`).then((response: AxiosResponse<any>) => {
    const resp = response.data
    return resp?.responseObject || resp
  })
}

const createReel = (reel: Partial<Reel>): Promise<Reel | undefined> => {
  return axios
    .post(`${API_URL}/reels`, reel)
    .then((response: AxiosResponse<Response<Reel>>) => response.data)
    .then((response: Response<Reel>) => response.data)
}

const updateReel = (reel: Partial<Reel>, id: ID): Promise<Reel | undefined> => {
  return axios
    .put(`${API_URL}/reels/${id}`, reel)
    .then((response: AxiosResponse<Response<Reel>>) => response.data)
    .then((response: Response<Reel>) => response.data)
}

const deleteSelectedReels = (reelIds: Array<ID>): Promise<void> => {
  const requests = reelIds.map((id) => axios.delete(`${API_URL}/reels/${id}`))
  return axios.all(requests).then(() => {})
}

const getGames = (query?: string): Promise<Response<Array<Game>>> => {
  const mappedQuery = query ? query.replace(/items_per_page=/g, 'limit=') : ''
  const url = `${API_URL}/games${mappedQuery ? `?${mappedQuery}` : ''}`

  return axios.get(url).then((response: AxiosResponse<any>) => {
    const resp = response.data

    const games = resp?.responseObject?.data || resp.data || []
    const paginationRaw = resp?.responseObject?.pagination ||
      resp.pagination || {
        current_page: 1,
        limit: 15,
        total: 0,
        total_pages: 0,
      }

    const totalPages = paginationRaw.total_pages || 0
    const currentPage = paginationRaw.current_page || 1

    const links: Array<{label: string; active: boolean; url: string | null; page: number | null}> =
      []

    links.push({
      label: '&laquo; Previous',
      active: false,
      url: null,
      page: currentPage > 1 ? currentPage - 1 : null,
    })

    for (let i = 1; i <= totalPages; i++) {
      links.push({label: String(i), active: i === currentPage, url: null, page: i})
    }

    links.push({
      label: 'Next &raquo;',
      active: false,
      url: null,
      page: currentPage < totalPages ? currentPage + 1 : null,
    })

    const gamesMapped: Array<Game> = games.map((game: any) => ({
      ...game,
      id: game.id?.toString() || '',
      name: game.title || game.name || '',
      date: game.event_start_datetime || game.date || '',
      location_name: game.location_name,
      online_url: game.online_url,
      location: game.location_name || game.online_url || (game.is_online ? 'Online' : 'Offline'),
      status: game.status || '',
      created_at: game.created_at || '',
      description: game.description || '',
      fee: game.fee,
      from_time: game.from_time,
      to_time: game.to_time,
      created_by: game.created_by || {
        id: game.user_id || 0,
        first_name: game.first_name || '',
        last_name: game.last_name || '',
        email: game.email || '',
        phone: game.phone || '',
      },
    }))

    return {
      data: gamesMapped,
      payload: {
        pagination: {
          page: currentPage,
          items_per_page: paginationRaw.limit || 15,
          total: paginationRaw.total || 0,
          total_pages: totalPages,
          links,
        },
      },
    } as Response<Array<Game>>
  })
}
// Dashboard API
const getDashboard = (range?: string, startDate?: string, endDate?: string): Promise<any> => {
  let url = `${API_URL}/dashboard`
  const params = new URLSearchParams()
  if (range === 'custom' && startDate && endDate) {
    params.append('range', 'custom')
    params.append('startDate', startDate)
    params.append('endDate', endDate)
  } else if (range) {
    params.append('range', range)
  }
  if (params.toString()) url += `?${params.toString()}`

  console.log('Dashboard URL:', url)

  return axios.get(url).then((response: AxiosResponse<any>) => {
    const resp = response.data
    return resp?.responseObject ?? resp
  })
}

const getDashboardData = (range?: string, startDate?: string, endDate?: string): Promise<any> => {
  let url = `${API_URL}/dashboard`
  const params = new URLSearchParams()
  if (range === 'custom' && startDate && endDate) {
    params.append('range', 'custom')
    params.append('startDate', startDate)
    params.append('endDate', endDate)
  } else if (range) {
    params.append('range', range)
  }
  if (params.toString()) url += `?${params.toString()}`

  return axios.get(url).then((response: AxiosResponse<any>) => {
    const resp = response.data
    return resp?.responseObject ?? resp
  })
}

// New Role-based Permissions APIs
const normalizePostSharePermission = (permissionsData: any) => {
  const data = {
    ...permissionsData,
    permissions: {
      ...(permissionsData?.permissions || {}),
    },
  }

  const postPermissions = data.permissions?.post
  if (postPermissions && Object.prototype.hasOwnProperty.call(postPermissions, 'save')) {
    data.permissions.post = {
      ...postPermissions,
      share: postPermissions.share,
    }
    delete data.permissions.post.share
  }

  return data
}

const normalizeRolePermissionsResponse = (permissions: any) => {
  if (!Array.isArray(permissions)) return permissions

  return permissions.map((permission) => ({
    ...permission,
    permissions: normalizePostSharePermission({permissions: permission.permissions}).permissions,
  }))
}

const getRolePermissionsByUserType = (userType: string): Promise<any> => {
  return axios
    .get(`${API_URL}/permissions?${userType}`)
    .then((response: AxiosResponse<any>) => {
      const resp = response.data
      return normalizeRolePermissionsResponse(resp?.responseObject ?? resp)
    })
    .catch((error) => {
      console.error(`Failed to fetch permissions for user type: ${userType}`, error)
      throw error
    })
}

const getAllUserTypes = (): Promise<any> => {
  return axios
    .get(`${API_URL}/permissions/user-types`)
    .then((response: AxiosResponse<any>) => {
      const resp = response.data
      return resp?.responseObject ?? resp
    })
    .catch((error) => {
      console.error('Failed to fetch user types', error)
      throw error
    })
}

const getUserTypeById = (userTypeSlug: string): Promise<any> => {
  return axios
    .get(`${API_URL}/permissions/user-types/${userTypeSlug}`)
    .then((response: AxiosResponse<any>) => {
      const resp = response.data
      return resp?.responseObject ?? resp
    })
    .catch((error) => {
      console.error(`Failed to fetch user type: ${userTypeSlug}`, error)
      throw error
    })
}

const updateRolePermissions = (userType: string, permissionsData: any): Promise<any> => {
  const normalizedPermissionsData = normalizePostSharePermission(permissionsData)

  return axios
    .put(`${API_URL}/permissions/${userType}`, normalizedPermissionsData)
    .then((response: AxiosResponse<any>) => {
      const resp = response.data
      return resp?.responseObject ?? resp
    })
    .catch((error) => {
      console.error(`Failed to update permissions for user type: ${userType}`, error)
      throw error
    })
}

const getAllRolePermissions = async (): Promise<any[]> => {
  try {
    const userTypesRes = await getAllUserTypes()
    const userTypes = Array.isArray(userTypesRes) ? userTypesRes : []
    const allPermissions: any[] = []

    userTypes.forEach((type: any) => {
      allPermissions.push({
        id: type?.id || type?.slug,
        role_id: type?.id || type?.slug,
        name: type?.name || '',
        role_name: type?.name || '',
        slug: type?.slug || '',
        role_slug: type?.slug || '',
        permissions: {},
        module_list: [],
        users_count: 0,
        created_at: type?.created_at || '',
        isActive: true,
      })
    })

    return allPermissions
  } catch (error) {
    console.error('Failed to fetch all role permissions:', error)
    return []
  }
}

// Activity Modules API
const ACTIVITY_MODULE_OPTIONS = [
  {value: 'games', label: 'Games'},
  {value: 'auth', label: 'Auth'},
  {value: 'jobs', label: 'Jobs'},
  {value: 'events', label: 'Events'},
  {value: 'directories', label: 'Directories'},
  {value: 'chats', label: 'Chats'},
  {value: 'posts', label: 'Posts'},
]

const ACTIVITY_MODULE_KEY_MAP: Record<string, string> = {
  game: 'games',
  games: 'games',
  auth: 'auth',
  job: 'jobs',
  jobs: 'jobs',
  event: 'events',
  events: 'events',
  directory: 'directories',
  directories: 'directories',
  chat: 'chats',
  chats: 'chats',
  post: 'posts',
  posts: 'posts',
}

const getActivityModules = (): Promise<any[]> => {
  return axios
    .get(`${API_URL}/permissions`)
    .then((response: AxiosResponse<any>) => {
      const responseObject = response.data?.responseObject || []
      const moduleNames = new Set<string>()

      responseObject.forEach((userType: any) => {
        if (userType.permissions) {
          Object.keys(userType.permissions).forEach((moduleName: string) => {
            const normalizedModuleName = ACTIVITY_MODULE_KEY_MAP[moduleName.toLowerCase()]
            if (normalizedModuleName) {
              moduleNames.add(normalizedModuleName)
            }
          })
        }
      })

      if (moduleNames.size === 0) {
        return ACTIVITY_MODULE_OPTIONS
      }

      return ACTIVITY_MODULE_OPTIONS.filter(({value}) => moduleNames.has(value))
    })
    .catch((error) => {
      console.error('Failed to fetch activity modules:', error)
      return ACTIVITY_MODULE_OPTIONS
    })
}

const getDashboardActivityLogs = ({
  userId,
  startDate,
  endDate,
  modules,
  search,
  page = 1,
  limit = 20,
}: ActivityLogsParams): Promise<any> => {
  const basePath =
    userId !== undefined && userId !== null
      ? `${API_URL}/dashboard/activity-logs/${userId}`
      : `${API_URL}/dashboard/activity-logs`

  let url = `${basePath}?start_date=${encodeURIComponent(startDate || '')}&end_date=${encodeURIComponent(endDate || '')}&page=${page}&limit=${limit}`

  if (modules && modules.length > 0) {
    url += `&modules=${modules.join(',')}`
  }
  if (search && search.trim()) {
    url += `&search=${encodeURIComponent(search.trim())}`
  }

  return axios.get(url).then((response: AxiosResponse<any>) => {
    const resp = response.data
    const logs = resp?.responseObject?.data || resp?.responseObject || resp?.data || resp || []

    if (!Array.isArray(logs)) {
      return {
        data: [],
        pagination: resp?.responseObject?.pagination || {page, limit, total: 0, totalPages: 1},
      }
    }

    return {
      data: logs.map((log: any, index: number) => ({
        id: log?.id ?? log?._id ?? index + 1,
        user_id: log?.user_id ?? log?.userId ?? log?.user?.id,
        user_name: (() => {
          const fullName = [log?.user?.first_name, log?.user?.last_name]
            .filter(Boolean)
            .join(' ')
            .trim()
          return log?.user_name ?? log?.userName ?? log?.username ?? fullName ?? log?.user?.name
        })(),
        module: log?.module ?? log?.module_name ?? log?.moduleName ?? '',
        module_name: log?.module_name ?? log?.module ?? log?.moduleName ?? '',
        action: log?.action ?? log?.action_taken ?? log?.actionTaken ?? log?.activity ?? '',
        action_taken: log?.action_taken ?? log?.action ?? log?.actionTaken ?? log?.activity ?? '',
        description: log?.description ?? log?.message ?? log?.details ?? '',
        status: log?.status ?? log?.statusCode ?? log?.result ?? '',
        created_at: log?.created_at ?? log?.createdAt ?? log?.date ?? log?.timestamp ?? '',
        updated_at: log?.updated_at ?? log?.updatedAt,
      })),
      pagination: resp?.responseObject?.pagination || {
        page,
        limit,
        total: logs.length,
        totalPages: 1,
      },
    }
  })
}

type SendNotificationPayload = {
  title: string
  body: string
  user_types?: string[]
}

const sendNotificationToAllUsers = (data: SendNotificationPayload): Promise<any> => {
  return axios
    .post(`${API_URL}/dashboard/notification/all-users`, data)
    .then((response: AxiosResponse<any>) => {
      const resp = response.data
      return resp?.responseObject ?? resp
    })
}

const sendNotification = (data: SendNotificationPayload): Promise<any> => {
  return axios
    .post(`${API_URL}/dashboard/notification/all-users`, data)
    .then((response: AxiosResponse<any>) => {
      const resp = response.data
      return resp?.responseObject ?? resp
    })
}

// Notification API
const getAllNotifications = (query?: string): Promise<Response<Array<any>>> => {
  const mappedQuery = query ? query.replace(/items_per_page=/g, 'limit=') : ''
  const url = `${API_URL}/dashboard/notifications${mappedQuery ? `?${mappedQuery}` : ''}`

  return axios.get(url).then((response: AxiosResponse<any>) => {
    const resp = response.data

    const notifications = resp?.responseObject?.data || resp?.data || []
    const paginationRaw = resp?.responseObject?.pagination ||
      resp?.pagination || {
        page: 1,
        limit: 15,
        total: notifications.length,
        totalPages: 1,
      }

    const itemsPerPage =
      Number(paginationRaw.limit || paginationRaw.items_per_page || paginationRaw.per_page) || 15
    const total = Number(paginationRaw.total) || notifications.length || 0
    const currentPage =
      Number(
        paginationRaw.page ||
          paginationRaw.current_page ||
          paginationRaw.currentPage ||
          (paginationRaw.offset !== undefined
            ? Math.floor(Number(paginationRaw.offset) / itemsPerPage) + 1
            : undefined)
      ) || 1
    const totalPages =
      Number(
        paginationRaw.total_pages ||
          paginationRaw.totalPages ||
          paginationRaw.last_page ||
          (total > 0 ? Math.ceil(total / itemsPerPage) : 1)
      ) || 1

    const links: Array<{label: string; active: boolean; url: string | null; page: number | null}> =
      []

    links.push({
      label: '&laquo; Previous',
      active: false,
      url: null,
      page: currentPage > 1 ? currentPage - 1 : null,
    })

    // Page number links
    for (let i = 1; i <= totalPages; i++) {
      links.push({label: String(i), active: i === currentPage, url: null, page: i})
    }

    // Next link
    links.push({
      label: 'Next &raquo;',
      active: false,
      url: null,
      page: currentPage < totalPages ? currentPage + 1 : null,
    })

    return {
      data: notifications,
      payload: {
        pagination: {
          page: currentPage,
          items_per_page: itemsPerPage,
          total,
          total_pages: totalPages,
          links,
        },
      },
    }
  })
}

export {
  getUsers,
  getUsersListForEvent,
  getUsersByUserType,
  getRoleListForUsers,
  getUserById,
  assignRole,
  getRoleView,
  approveUser,
  deleteUser,
  deleteSelectedUsers,
  createUser,
  updateUser,
  updateStatus,
  isNDISVerification,
  getServiceProviders,
  getServiceProviderById,
  getEvents,
  getJobs,
  getJobById,
  createJob,
  updateJob,
  approveJob,
  updateJobStatus,
  getEventById,
  createEvent,
  updateEvent,
  deleteEvent,
  deleteSelectedEvents,
  eventApproval,
  rejectEvent,
  getEventCategories,
  getRoles,
  getRoleById,
  createRole,
  updateRole,
  deleteRole,
  deleteSelectedRoles,
  getRoleList,
  getPermissionList,
  getPermissionView,
  getModules,
  createMasterRole,
  createMasterPermission,
  updateMasterRole,
  updateMasterPermission,
  updateMasterRoleStatus,
  updateMasterPermissionStatus,
  updateRoleStatus,
  isForcefullyLogout,
  getBusinessSector,
  getBusinessType,
  getEventCategoriess,
  getEventCategoryById,
  createEventCategory,
  updateEventCategory,
  updateEventCategoryStatus,
  getInterestSuggestions,
  getServiceSuggestions,
  getSkillSuggestions,
  getBlockUsers,
  blockUser,
  getBlockerUsers,
  getBlockedUsers,
  unblockUser,
  getSkills,
  getSkillById,
  createSkill,
  updateSkill,
  updateSkillStatus,
  // deleteSkill,
  // deleteSelectedSkills,
  skillApproval,
  approveSkill,
  rejectSkill,
  getInterests,
  getInterestById,
  createInterest,
  updateInterest,
  updateInterestStatus,
  interestApproval,
  approveInterest,
  rejectInterest,
  getUsersListForJob,
  getJobsApplicant,
  getReelById,
  getReels,
  createReel,
  updateReel,
  deleteSelectedReels,
  getGames,
  getGameById,
  createGame,
  updateGame,
  updateGameStatus,
  toggleGameInterest,
  getDashboard,
  getDashboardData,
  getRolePermissionsByUserType,
  getAllUserTypes,
  getUserTypeById,
  updateRolePermissions,
  getAllRolePermissions,
  getDashboardActivityLogs,
  getAllNotifications,
  sendNotification,
  sendNotificationToAllUsers,
  getActivityModules,
}
